import { useEffect, useState, type RefObject, type TransitionEvent } from "react";

// Two-phase morph: width grows first, then height. Each phase uses `durationMs`; the
// transitionend handler chains them, with a fallback timer (durationMs + buffer) for the
// no-transition case (pattern borrowed from CreateTodoSheet's ExpandableAccordion).
const FALLBACK_BUFFER_MS = 80;

export function useTwoPhaseMorph(open: boolean, morphRef: RefObject<HTMLDivElement | null>, durationMs: number) {
  // `wide` (width phase) leads on open; `tall` (height phase) leads on close. The morph box's
  // transitionend advances open: wide -> tall, and close: tall -> wide.
  const [wide, setWide] = useState(false);
  const [tall, setTall] = useState(false);

  // Drive the phases off `open`. On open start the width phase; on close start the height phase.
  useEffect(() => {
    if (open) setWide(true);
    else setTall(false);
  }, [open]);

  // Fallback if the leading transition produces no change (and thus no transitionend).
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (open) setTall(true);
      else setWide(false);
    }, durationMs + FALLBACK_BUFFER_MS);
    return () => window.clearTimeout(id);
  }, [open, durationMs]);

  const onTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target !== morphRef.current) return;
    if (open && event.propertyName === "width") setTall(true);
    if (!open && event.propertyName === "height") setWide(false);
  };

  return { wide, tall, onTransitionEnd };
}
